// Physical destruction debris (render-only; gameplay stays in the deterministic Sim). MAP-DESTRUCTION (owner 2026-11-02:
// 'more map destruction for each skill, similar to how the enemies are impacted… disk could cut trees or rocks in half /
// lasers can cut thru rocks or trees and they should crumble', then 'make the destruction much more realistic'):
//  - three-pinata pre-fractures each rock shape twice: an impact-weighted Voronoi ('shatter': a few big slabs on the far
//    side, many small fragments where it was hit) and a fine uniform one ('crumble'); Scythe Disc cuts slice the actual
//    prop mesh along the blade's plane at break time (budgeted per frame)
//  - Rapier simulates every piece as a convex rigid body over a heightfield matching the cratered ground; pieces collide
//    with each other, tumble, settle and stay ~6–10 s, then sink (oldest evicted first under the cap)
//  - each PROP_SRC has its own break: blast = blown apart away from the centre (trees uprooted + charred, root hole),
//    beam / pierce = glowing hot seams, then the rock crumbles and cools (trees cut, char and fall to ash), cut = clean
//    slice (top slides off the stump / the half slides away), vortex = lifted into the Singularity, wall = heaved up and
//    tossed forward, chip / crush / burn = toppled about a hinge at the base, spires collapse in sections over ~1 s
//  - lasting marks: stumps (pale cut wood / charred) and rubble piles stay as cheap static instances
// Hard caps keep phones safe (chunk cap per quality, slice budget, pooled leaves / rubble / stumps).
import * as THREE from 'three/webgpu';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
// LOAD-PERF (owner 2026-11-02 'load quickly'): Rapier (≈ 4 MB of JS with its WASM inlined) and three-pinata are loaded on
// demand, off the critical path: boot starts the download first thing (loadPhysics()), the title appears without waiting,
// and the loading screen's warm-up waits for it (debris pieces compile before the first run).
import type * as RT from '@dimforge/rapier3d-compat';
import type * as PinataNS from '@dgreenheck/three-pinata';
let RAPIER: typeof RT['default'];
let DestructibleMesh: typeof PinataNS.DestructibleMesh, FractureOptions: typeof PinataNS.FractureOptions;
let physicsP: Promise<void> | null = null;
/** Start (once) loading the physics + fracture libraries; resolves when both are ready (Rapier's WASM initialised). */
export function loadPhysics(): Promise<void> {
  return physicsP ??= Promise.all([import('@dimforge/rapier3d-compat'), import('@dgreenheck/three-pinata')]).then(async ([r, p]) => {
    RAPIER = r.default; DestructibleMesh = p.DestructibleMesh; FractureOptions = p.FractureOptions;
    await RAPIER.init();
  });
}
import { attribute, color, fract, length, mix, mx_noise_float, positionGeometry, smoothstep, step, time, uniform, vec3 } from 'three/tsl';
import { PROP_SRC, type Prop, type PropKind } from '../core/terrain';
import { GroundDeform, GROUND_HALF } from './ground';
import { canyonMaterial } from './canyon';
import { outlineGeometry, outlineMaterial, RenderStyle, toonMaterial } from './toon';
import type { TreePart } from './trees';
import { BUSH_K, trunkRadius, type FloraShape } from './flora';
import type { Fx } from './fx';
import { DEBRIS } from './debrisLook';

type RockKind = 'rock' | 'boulder' | 'spire';
interface Template { geo: THREE.BufferGeometry; offset: THREE.Vector3; hull: Float32Array; vol: number; ny: number; line: THREE.BufferGeometry | null }
/** PERF PASS 3 (memory): geometries the templates create for themselves (cut trunks, outlines, cut faces) — disposed on retarget */
const _own = (g: THREE.BufferGeometry, into: THREE.BufferGeometry[]): THREE.BufferGeometry => { into.push(g); return g; };
interface Hinge { px: number; py: number; pz: number; ax: number; az: number; th: number; w: number; k: number; q0: THREE.Quaternion; p0: THREE.Vector3 }
interface Hot { mat: THREE.Material; heat: { value: number }; tint: { value: THREE.Color }; t0: number; dur: number }
interface Chunk {
  mesh: THREE.Object3D; body: RT.RigidBody; col: RT.Collider | null; age: number; life: number; sink: number;
  /** seconds held in place (kinematic) before release; rv = release velocity (vx vy vz ax ay az) */
  hold: number; rv: Float32Array | null;
  /** Singularity pull: 1 = being sucked toward (tx, ty, tz) */
  pull: number; tx: number; ty: number; tz: number;
  hinge: Hinge | null; bs: THREE.Vector3 | null;
  /** tree piece: S = tree scale (vertical), impact 0 waiting / 1 landed; ash = crumbles to ash after landing (ashT countdown);
   *  cy / top = the variant's canopy centre and crown top (local) */
  tree: boolean; S: number; impact: number; ash: boolean; ashT: number; cy: number; top: number;
  /** sliced spire top: crumbles on landing (kept: local offset of the piece inside the spire, and the spire's scale) */
  spireTop: THREE.Vector3 | null; spireS: THREE.Vector3 | null;
  own: THREE.BufferGeometry | null; ownLine: THREE.BufferGeometry | null;
  kind: PropKind; size: number;
}

const HF_N = 176; // heightfield cells per side for the physics ground (~2 m cells: was 128 over 260 m, the ground is 360 m now)
const G = 26;
const _v = new THREE.Vector3(), _w = new THREE.Vector3(), _u = new THREE.Vector3(), _q = new THREE.Quaternion(), _q2 = new THREE.Quaternion(), _s = new THREE.Vector3(), _p = new THREE.Vector3(), _m = new THREE.Matrix4(), _n = new THREE.Vector3(), _e = new THREE.Euler();
const UP = new THREE.Vector3(0, 1, 0);
const rnd = (a: number, b: number) => a + Math.random() * (b - a);

// painted tree trunk profile (trees.ts paintedTree: trunkGeo(1.5, 0.17, 0.12) with the root flare below 0.35) — local units
const TRUNK_H = 1.5, TRUNK_R0 = 0.17, TRUNK_R1 = 0.12;
const trunkR = (y: number) => (TRUNK_R0 + (TRUNK_R1 - TRUNK_R0) * (y / TRUNK_H)) * (y < 0.35 ? 1 + (0.35 - y) * 0.9 : 1);
/** canopy centre height of the classic tree (local; other variants carry their own in FloraShape) */
const CANOPY_Y = 2.9;
/** cut heights (local units, × tree scale ≈ 2–3 m per unit): disc blade, laser, base (felled / burnt) */
const CUT = { disc: 0.36, laser: 0.5, base: 0.1 };
const STUMP_H = 0.4;
// leaf / ash / clod / chip / rubble colours come from the map's debris palette (debrisLook.ts DEBRIS)

/** Falling leaves: small double-sided cards that flutter down (trees hit, felled or landing). Pooled, allocation-free. */
class Leaves {
  readonly mesh: THREE.InstancedMesh;
  private d: Float32Array; private n = 0; private static S = 10; // x y z vx vy vz phase spin life size
  constructor(private cap: number, private groundAt: (x: number, z: number) => number) {
    this.d = new Float32Array(cap * Leaves.S);
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute([0, 0, -0.5, 0.32, 0, 0, 0, 0, 0.5, -0.32, 0, 0], 3));
    g.setIndex([0, 1, 2, 0, 2, 3]); g.computeVertexNormals();
    this.mesh = new THREE.InstancedMesh(g, new THREE.MeshBasicNodeMaterial({ side: THREE.DoubleSide }), cap);
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(cap * 3), 3);
    this.mesh.frustumCulled = false; this.mesh.count = 0;
  }
  /** map palette puff (snow shaken off the branches): called once per non-ash emit */
  puff: ((x: number, y: number, z: number, n: number, spread: number) => void) | null = null;
  emit(x: number, y: number, z: number, n: number, spread: number, up: number, ash = false): void {
    const c = this.mesh.instanceColor!.array as Float32Array;
    if (!ash && this.puff) this.puff(x, y, z, n, spread);
    for (let k = 0; k < n; k++) {
      if (this.n >= this.cap) return;
      const i = this.n * Leaves.S, d = this.d, a = Math.random() * 6.283, r = Math.random() * spread;
      d[i] = x + Math.cos(a) * r; d[i + 1] = y + (Math.random() - 0.3) * spread * 0.6; d[i + 2] = z + Math.sin(a) * r;
      d[i + 3] = Math.cos(a) * rnd(0.5, 2.5); d[i + 4] = up * Math.random(); d[i + 5] = Math.sin(a) * rnd(0.5, 2.5);
      d[i + 6] = Math.random() * 6.283; d[i + 7] = rnd(2, 6); d[i + 8] = rnd(2.5, 4.5); d[i + 9] = rnd(0.16, 0.3);
      const A = DEBRIS.look.ash, Lc = DEBRIS.leaves;
      if (ash) _c.setHex(A[k % A.length]); else _c.copy(Lc[k % Lc.length]).multiplyScalar(rnd(0.85, 1.15));
      c[this.n * 3] = _c.r; c[this.n * 3 + 1] = _c.g; c[this.n * 3 + 2] = _c.b;
      this.n++;
    }
  }
  clear(): void { this.n = 0; this.mesh.count = 0; }
  update(dt: number): void {
    const d = this.d, S = Leaves.S, c = this.mesh.instanceColor!.array as Float32Array;
    for (let k = 0; k < this.n; k++) {
      const i = k * S; d[i + 8] -= dt;
      if (d[i + 8] <= 0) { this.n--; if (k !== this.n) { d.copyWithin(i, this.n * S, this.n * S + S); c.copyWithin(k * 3, this.n * 3, this.n * 3 + 3); } k--; continue; }
      d[i + 6] += dt * 3.2;
      // flutter: strong air drag, slow fall, a sideways sway
      const dr = Math.exp(-2.4 * dt); d[i + 3] *= dr; d[i + 5] *= dr; d[i + 4] = d[i + 4] * Math.exp(-3 * dt) - 3.2 * dt;
      d[i] += (d[i + 3] + Math.sin(d[i + 6]) * 0.9) * dt; d[i + 1] += d[i + 4] * dt; d[i + 2] += (d[i + 5] + Math.cos(d[i + 6] * 0.8) * 0.6) * dt;
      const gy = this.groundAt(d[i], d[i + 2]) + 0.03; if (d[i + 1] < gy) { d[i + 1] = gy; d[i + 3] = d[i + 4] = d[i + 5] = 0; d[i + 7] = 0; }
    }
    for (let k = 0; k < this.n; k++) {
      const i = k * S, f = Math.min(1, d[i + 8] / 0.6), s = d[i + 9] * f;
      _e.set(Math.sin(d[i + 6]) * 1.2, d[i + 6] * d[i + 7] * 0.2, Math.cos(d[i + 6] * 1.3) * 0.9); _q.setFromEuler(_e);
      _p.set(d[i], d[i + 1], d[i + 2]); _s.set(s, s, s); this.mesh.setMatrixAt(k, _m.compose(_p, _q, _s));
    }
    this.mesh.count = this.n; this.mesh.instanceMatrix.needsUpdate = true; this.mesh.instanceColor!.needsUpdate = true;
  }
}
const _c = new THREE.Color();

/** Static instanced bits on the ground (rubble piles, ash, stumps): ring buffer, matrices written once per add and re-seated
 *  on the healing ground about once a second. */
class Statics {
  readonly meshes: THREE.InstancedMesh[] = [];
  private next = 0; private used = 0;
  private d: Float32Array; private static S = 8; // x z yOff yaw sx sy sz tilt
  constructor(private cap: number, parts: [THREE.BufferGeometry, THREE.Material][], private groundAt: (x: number, z: number) => number, shadows: boolean) {
    this.d = new Float32Array(cap * Statics.S);
    for (const [g, m] of parts) {
      const im = new THREE.InstancedMesh(g, m, cap);
      im.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(cap * 3).fill(1), 3);
      im.frustumCulled = false; im.count = 0; im.castShadow = shadows; im.receiveShadow = shadows;
      this.meshes.push(im);
    }
  }
  add(x: number, z: number, yOff: number, yaw: number, sx: number, sy: number, sz: number, col: THREE.Color, tilt = 0): void {
    const k = this.next; this.next = (this.next + 1) % this.cap; this.used = Math.min(this.cap, this.used + 1);
    const i = k * Statics.S, d = this.d;
    d[i] = x; d[i + 1] = z; d[i + 2] = yOff; d[i + 3] = yaw; d[i + 4] = sx; d[i + 5] = sy; d[i + 6] = sz; d[i + 7] = tilt;
    for (const im of this.meshes) { const c = im.instanceColor!.array as Float32Array; c[k * 3] = col.r; c[k * 3 + 1] = col.g; c[k * 3 + 2] = col.b; im.instanceColor!.needsUpdate = true; }
    this.seat(k);
    for (const im of this.meshes) { im.count = this.used; im.instanceMatrix.needsUpdate = true; }
  }
  private seat(k: number): void {
    const i = k * Statics.S, d = this.d;
    _e.set(d[i + 7], d[i + 3], d[i + 7] * 0.6); _q.setFromEuler(_e);
    _p.set(d[i], this.groundAt(d[i], d[i + 1]) + d[i + 2], d[i + 1]); _s.set(d[i + 4], d[i + 5], d[i + 6]);
    _m.compose(_p, _q, _s); for (const im of this.meshes) im.setMatrixAt(k, _m);
  }
  reseat(): void { for (let k = 0; k < this.used; k++) this.seat(k); for (const im of this.meshes) im.instanceMatrix.needsUpdate = true; }
  clear(): void { this.next = 0; this.used = 0; for (const im of this.meshes) im.count = 0; }
}

export class Debris {
  private world: RT.World | null = null;
  private groundCollider: RT.Collider | null = null;
  private groundVersion = -1;
  private hfBuf = new Float32Array((HF_N + 1) * (HF_N + 1));
  private sinceGround = 0;
  private templates = new Map<string, Template[]>();
  private sliceSrc = new Map<RockKind, THREE.BufferGeometry>();
  private mats = new Map<PropKind, THREE.Material[]>();
  private hot = new Map<RockKind, Hot[]>();
  private chunks: Chunk[] = [];
  private stumpCols: { col: RT.Collider; t: number }[] = [];
  private treeTpl = new Map<string, THREE.Group>();
  private tplGeos: THREE.BufferGeometry[] = [];
  private leaves: Leaves; private rubble: Statics; private stumps: Statics; private brush: Statics;
  private bushTpl = new Map<string, THREE.Group>();
  private acc = 0; private clock = 0; private sliceBudget = 2; private reseatT = 0;
  private outlines: boolean;
  /** set by the view: effects sink (dust, gravel) and the live Singularity orb nearest to a point */
  fx: Fx | null = null;
  orbAt: ((x: number, z: number, out: THREE.Vector3) => boolean) | null = null;
  ready = false;

  constructor(private scene: THREE.Scene, private ground: GroundDeform, private style: RenderStyle, private cap: number,
    private sources: { rock: THREE.BufferGeometry; boulder: THREE.BufferGeometry; spire: THREE.BufferGeometry; tree: THREE.Object3D; treeParts?: TreePart[]; cel?: boolean; trees?: FloraShape[]; bushes?: FloraShape[] },
    quality = 'high') {
    const low = quality === 'low', gy = (x: number, z: number) => this.ground.sample(x, z);
    this.outlines = style === 'cel' && !low;
    this.leaves = new Leaves(low ? 160 : 360, gy);
    this.leaves.puff = (x, y, z, n, spread) => { // per-map puffs (glacier: snow off the branches), debrisLook.ts
      const col = DEBRIS.look.puff, b = this.fx?.blast; if (col === undefined || !b) return;
      for (let k = 0, m = Math.min(6, 1 + Math.round(n / 5)); k < m; k++) {
        const a = Math.random() * 6.283, r = Math.random() * spread * 0.7;
        b.puff(x + Math.cos(a) * r, y + rnd(-0.3, 0.3) * spread, z + Math.sin(a) * r, Math.cos(a) * rnd(0.3, 1.2), rnd(-0.6, 0.4), Math.sin(a) * rnd(0.3, 1.2), rnd(1.2, 2.2), spread * 0.25 + 0.3, spread * 0.7 + rnd(0.6, 1.2), col);
      }
    };
    const cel = style === 'cel';
    const m = (c: number) => cel ? toonMaterial({ color: c }) : new THREE.MeshStandardNodeMaterial({ color: c, roughness: 0.95, flatShading: true });
    const pebble = (() => { const g0 = new THREE.IcosahedronGeometry(1, 0); const pos = g0.attributes.position as THREE.BufferAttribute; for (let i = 0; i < pos.count; i++) pos.setY(i, pos.getY(i) * 0.55); g0.computeVertexNormals(); return g0; })();
    this.rubble = new Statics(low ? 140 : 320, [[pebble, m(0xffffff)]], gy, !low);
    // stump: the lower trunk (bark, root flare) + a pale cut face with growth rings; scaled in y to the cut height
    const st = new THREE.CylinderGeometry(trunkR(STUMP_H), TRUNK_R0, STUMP_H, 14, 3).translate(0, STUMP_H / 2, 0);
    { const pos = st.attributes.position as THREE.BufferAttribute; for (let i = 0; i < pos.count; i++) { const y = pos.getY(i), k = y < 0.35 ? 1 + (0.35 - y) * 0.9 : 1; pos.setX(i, pos.getX(i) * k); pos.setZ(i, pos.getZ(i) * k); } st.computeVertexNormals(); }
    const face = new THREE.CircleGeometry(trunkR(STUMP_H) * 0.97, 16).rotateX(-Math.PI / 2).translate(0, STUMP_H + 0.002, 0);
    const bark = sources.treeParts?.[0]?.mat ?? m(0x7a5236);
    const parts: [THREE.BufferGeometry, THREE.Material][] = [[st, bark], [face, this.woodMat()]];
    if (this.outlines) parts.push([outlineGeometry(st), outlineMaterial(0.02)]);
    this.stumps = new Statics(low ? 32 : 72, parts, gy, !low);
    // bush stubble: a few snapped twigs splayed from the root (what a shredded / cut / burnt bush leaves behind)
    const twigs: THREE.BufferGeometry[] = [];
    for (let k = 0; k < 7; k++) {
      const a = k * 2.4, tl = 0.18 + (k % 3) * 0.07, g0 = new THREE.ConeGeometry(0.022, tl, 4).translate(0, tl / 2, 0);
      g0.rotateZ(0.25 + (k % 2) * 0.3).rotateY(a).translate(Math.cos(a) * 0.06, 0, Math.sin(a) * 0.06); g0.deleteAttribute('uv'); twigs.push(g0.toNonIndexed());
    }
    const tw = mergeGeometries(twigs, false)!; tw.computeVertexNormals();
    this.brush = new Statics(low ? 40 : 110, [[tw, bark]], gy, false);
    scene.add(this.leaves.mesh, ...this.rubble.meshes, ...this.stumps.meshes, ...this.brush.meshes);
  }

  /** pale wood with growth rings (cut faces, stumps) */
  private woodMat(): THREE.Material {
    const m = this.style === 'cel' ? toonMaterial({ color: 0xe0c494 }) : new THREE.MeshStandardNodeMaterial({ color: 0xe0c494, roughness: 0.9 });
    const r = length(positionGeometry.xz);
    (m as THREE.NodeMaterial).colorNode = mix(color(0xe6cc9c), color(0xb88f5f), step(0.6, fract(r.mul(48).add(mx_noise_float(positionGeometry.mul(22)).mul(0.35))))).mul(mix(1, 0.75, smoothstep(0.1, 0.17, r)));
    return m;
  }
  /** near-black char with glowing ember specks (charred trees / tree pieces) */
  private charMat(src: THREE.Material, leaf: boolean): THREE.Material {
    const ember = step(0.8, mx_noise_float(positionGeometry.mul(leaf ? 6 : 14).add(vec3(0, time.mul(0.35), 0)))).mul(0.85);
    const ash = mix(color(0x2a221d), color(0x4d4037), mx_noise_float(positionGeometry.mul(9)).mul(0.5).add(0.5));
    if (leaf) { // scorched leaf cards keep their light / shade bands (sooty brown-grey), with glowing ember specks
      const c = (src as THREE.NodeMaterial).clone() as THREE.NodeMaterial;
      c.opacityNode = (src as THREE.NodeMaterial).opacityNode;
      const k = attribute('normal', 'vec3').y.mul(0.5).add(0.5);
      c.colorNode = mix(mix(color(0x241d19), color(0x5e4d3f), smoothstep(0.4, 0.62, k)), color(0xff6a1c), ember);
      return c;
    }
    const m = this.style === 'cel' ? toonMaterial({ color: 0x241b16 }) : new THREE.MeshStandardNodeMaterial({ color: 0x241b16, roughness: 1 });
    (m as THREE.NodeMaterial).colorNode = ash;
    (m as unknown as { emissiveNode: unknown }).emissiveNode = color(0xff5a14).mul(ember);
    return m;
  }

  /** MAP SYSTEM: per-map stone materials for the pieces (outer, fracture face); set before init or via retarget() */
  private matOverride: Partial<Record<RockKind, [THREE.Material, THREE.Material]>> = {};
  private fractureGen = 0;
  /** pre-fracture the rock / boulder / spire shapes: essential set first, the rest in later macrotasks */
  private async prefracture(): Promise<void> {
    const gen = ++this.fractureGen;
    for (const k of ['rock', 'boulder', 'spire'] as RockKind[]) this.sliceSrc.set(k, this.prep(this.sources[k]));
    this.templates.set('rock:shatter', this.fracture('rock', 8, 11, true));
    this.templates.set('boulder:shatter', this.fracture('boulder', 14, 23, true));
    this.templates.set('spire:shatter', this.fracture('spire', 22, 37, true));
    this.ready = true;
    const rest: [string, RockKind, number, number][] = [['rock:crumble', 'rock', 13, 5], ['boulder:crumble', 'boulder', 24, 7], ['spire:crumble', 'spire', 32, 9]];
    for (const [key, kind, n, seed] of rest) { await new Promise((r) => setTimeout(r, 30)); if (gen !== this.fractureGen) return; this.templates.set(key, this.fracture(kind, n, seed, false)); }
  }
  /** MAP SYSTEM: a new map's prop shapes (and optional piece materials): drops cached pieces / templates and re-fractures. */
  retarget(sources: Debris['sources'], mats: Partial<Record<RockKind, [THREE.Material, THREE.Material]>> = {}): void {
    this.clear();
    this.sources = sources; this.matOverride = mats;
    for (const g of this.tplGeos) g.dispose(); this.tplGeos.length = 0; // (the live pieces that used them were cleared above)
    for (const ts of this.templates.values()) for (const t of ts) { t.geo.dispose(); t.line?.dispose(); }
    this.treeTpl.clear(); this.bushTpl.clear(); this.templates.clear(); this.sliceSrc.clear();
    if (!this.world) return; // init() is still loading Rapier: it will use the new sources / materials
    const cel = this.style === 'cel';
    const m = (c: number) => cel ? toonMaterial({ color: c }) : new THREE.MeshStandardNodeMaterial({ color: c, roughness: 0.9, flatShading: true });
    this.mats.set('rock', mats.rock ?? [canyonMaterial(this.style, 1.02, false), m(0x6a5544)]);
    this.mats.set('boulder', mats.boulder ?? [canyonMaterial(this.style, 0.97, false), m(0x5f4c3c)]);
    this.mats.set('spire', mats.spire ?? [canyonMaterial(this.style), m(0x6e5236)]);
    this.ready = false; void this.prefracture();
  }

  /** Async: loads Rapier's WASM and pre-fractures the prop shapes. Safe to call once; failures leave debris disabled. */
  async init(): Promise<void> {
    try {
      await loadPhysics();
      this.world = new RAPIER.World({ x: 0, y: -G, z: 0 });
      const cel = this.style === 'cel';
      const m = (c: number) => cel ? toonMaterial({ color: c }) : new THREE.MeshStandardNodeMaterial({ color: c, roughness: 0.9, flatShading: true });
      // outer stone + darker inner fracture / cut face
      // outer faces wear the same canyon sandstone as the standing props; the broken / cut faces are darker raw stone
      this.mats.set('rock', this.matOverride.rock ?? [canyonMaterial(this.style, 1.02, false), m(0x6a5544)]);
      this.mats.set('boulder', this.matOverride.boulder ?? [canyonMaterial(this.style, 0.97, false), m(0x5f4c3c)]);
      this.mats.set('spire', this.matOverride.spire ?? [canyonMaterial(this.style), m(0x6e5236)]);
      this.rebuildGround();
      await this.prefracture();
    } catch (err) {
      console.warn('[debris] physics disabled:', err);
      this.ready = false;
    }
  }

  /** LOAD-PERF: one stand-in of every piece look this map can produce (each tree / bush variant plain + charred, the rock /
   *  boulder / spire pieces with their stone and hot-seam materials, the outline shell), for the loading-screen warm-up to
   *  compile. Templates are shared with the live pieces (same materials), so nothing compiles at the first break. The caller
   *  adds them to the scene only for the compile and removes them after. */
  warmSet(): THREE.Object3D[] {
    const out: THREE.Object3D[] = [];
    if (!this.ready) return out;
    const nT = Math.max(1, this.sources.trees?.length ?? 1), nB = this.sources.bushes?.length ?? 0;
    for (let v = 0; v < nT; v++) for (const charred of [false, true]) for (const cut of [0, CUT.base]) out.push(this.treeGroup(v, cut, charred));
    for (let v = 0; v < nB; v++) for (const charred of [false, true]) out.push(this.bushGroup(v, 0, charred));
    for (const kind of ['rock', 'boulder', 'spire'] as RockKind[]) {
      const t = this.templates.get(`${kind}:shatter`)?.[0], mats = this.mats.get(kind); if (!t || !mats) continue;
      for (const m of [mats, [mats[0], this.hotMat(kind, 0xff8a2a)]]) {
        const mesh = new THREE.Mesh(t.geo, m); mesh.castShadow = true;
        if (t.line) mesh.add(new THREE.Mesh(t.line, outlineMaterial(0.022)));
        out.push(mesh);
      }
    }
    for (const h of this.hot.values()) for (const x of h) x.heat.value = 0; // (hotMat() armed one; it is only a stand-in)
    return out;
  }

  private prep(src: THREE.BufferGeometry): THREE.BufferGeometry {
    const g = src.index ? src.toNonIndexed() : src.clone();
    g.deleteAttribute('uv'); g.computeVertexNormals();
    g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(g.attributes.position.count * 2), 2));
    return g;
  }

  /** impact = Voronoi seeds packed around local +X (small fragments there, big slabs opposite) */
  private fracture(kind: RockKind, count: number, seed: number, impact: boolean): Template[] {
    const g = this.sliceSrc.get(kind)!;
    g.computeBoundingBox(); const bb = g.boundingBox!, size = bb.getSize(new THREE.Vector3());
    const dm = new DestructibleMesh(g as never);
    const vo = impact ? { mode: '3D' as const, impactPoint: new THREE.Vector3(bb.max.x * 0.9, bb.min.y + size.y * (kind === 'spire' ? 0.2 : 0.5), 0), impactRadius: Math.max(size.x, size.z) * 0.45 } : { mode: '3D' as const };
    const frags = dm.fracture(new FractureOptions({ fractureMethod: 'voronoi', fragmentCount: count, voronoiOptions: vo, seed }));
    return frags.map((f) => this.toTemplate((f.geometry as unknown as THREE.BufferGeometry).clone(), bb, kind !== 'spire'));
  }

  private toTemplate(geo: THREE.BufferGeometry, bb: THREE.Box3, line: boolean): Template {
    geo.computeBoundingBox();
    const c = geo.boundingBox!.getCenter(new THREE.Vector3());
    geo.translate(-c.x, -c.y, -c.z);
    geo.computeVertexNormals();
    const pos = geo.attributes.position as THREE.BufferAttribute;
    const step = Math.max(1, Math.floor(pos.count / 48)); // hull points: ≤ 48 for cheap convex hulls
    const pts: number[] = [];
    for (let i = 0; i < pos.count; i += step) pts.push(pos.getX(i), pos.getY(i), pos.getZ(i));
    const size = geo.boundingBox!.getSize(new THREE.Vector3());
    const ny = (c.y - bb.min.y) / Math.max(1e-4, bb.max.y - bb.min.y);
    return { geo, offset: c, hull: new Float32Array(pts), vol: size.x * size.y * size.z, ny, line: line && this.outlines ? outlineGeometry(geo) : null };
  }

  /** Physics ground follows the cratered height map (rebuilt at most twice a second when it changes). */
  private rebuildGround(): void {
    if (!this.world) return;
    const n = HF_N, h = this.hfBuf; // reused (Rapier copies the heights into the collider)
    for (let xi = 0; xi <= n; xi++) for (let zi = 0; zi <= n; zi++) {
      const x = -GROUND_HALF + (xi / n) * GROUND_HALF * 2, z = -GROUND_HALF + (zi / n) * GROUND_HALF * 2;
      h[xi * (n + 1) + zi] = this.ground.sample(x, z);
    }
    if (this.groundCollider) this.world.removeCollider(this.groundCollider, false);
    this.groundCollider = this.world.createCollider(RAPIER.ColliderDesc.heightfield(n, n, h, { x: GROUND_HALF * 2, y: 1, z: GROUND_HALF * 2 }).setFriction(0.9));
    this.groundVersion = this.ground.version;
  }

  private hotMat(kind: RockKind, tint: number): THREE.Material {
    let pool = this.hot.get(kind);
    if (!pool) {
      pool = [];
      const base = (this.mats.get(kind) ?? [])[1];
      const inner = base ? (base as THREE.MeshToonNodeMaterial).color?.getHex?.() ?? 0x6a5544 : 0x6a5544;
      for (let k = 0; k < 4; k++) {
        const heat = uniform(0), tintU = uniform(new THREE.Color(0xff8a2a));
        const m = this.style === 'cel' ? toonMaterial({ color: inner }) : new THREE.MeshStandardNodeMaterial({ color: inner, roughness: 0.9, flatShading: true });
        // white-hot → tint → dull red as it cools (heat 1 → 0); squared so the glow drops off quickly after the first instant
        const h = heat;
        const ramp = mix(mix(vec3(0.35, 0.04, 0.0), tintU, smoothstep(0.15, 0.55, h)), vec3(1, 0.95, 0.85), smoothstep(0.75, 1.0, h));
        (m as unknown as { emissiveNode: unknown }).emissiveNode = ramp.mul(h.mul(h).mul(1.7).add(h.mul(0.25)));
        pool.push({ mat: m, heat: heat as unknown as { value: number }, tint: tintU as unknown as { value: THREE.Color }, t0: -99, dur: 1.6 });
      }
      this.hot.set(kind, pool);
    }
    let best = pool[0]; for (const h of pool) if (h.t0 < best.t0) best = h;
    best.t0 = this.clock; best.dur = 1.6; best.tint.value.setHex(tint); best.heat.value = 1;
    return best.mat;
  }

  /** small-hit feedback (bolts, barrage, rush, the first hits of anything): leaves shake loose, rock chips fly */
  hitProp(p: Prop, S: number, src: number): void {
    if (p.kind === 'tree') { const f = this.flora(p), cy = f ? f.canopyY * p.h : CANOPY_Y, a = Math.random() * 6.283; this.leaves.emit(p.x + Math.cos(a) * S * 0.6, cy * S + this.ground.sample(p.x, p.z), p.z + Math.sin(a) * S * 0.6, src === PROP_SRC.chip ? 5 : 3, S * 0.9, 0.5, p.char > 0.5); return; }
    if (p.kind === 'bush') { // a few leaves and a twig or two shake loose
      const f = this.flora(p), gy = this.ground.sample(p.x, p.z), y = gy + (f ? f.top * 0.7 : 0.8) * p.s * BUSH_K * p.h;
      this.leaves.emit(p.x, y, p.z, src === PROP_SRC.chip ? 5 : 3, p.r * 0.8, 0.8, p.char > 0.5);
      this.fx?.blast?.pebbles(p.x, y, p.z, 1, 0, 2, 0, 1.5, 0.04, DEBRIS.look.clod, 1.2);
      return;
    }
    const y = this.ground.sample(p.x, p.z) + (p.kind === 'spire' ? 1.5 : p.r * 0.5);
    this.fx?.blast?.pebbles(p.x, y, p.z, p.kind === 'spire' ? 4 : 2, 0, 3, 0, 3, p.kind === 'rock' ? 0.07 : 0.1, DEBRIS.look.chip, 1.2);
  }

  /** Roaring Meteor (owner 2026-11-03: 'dust and debris eruption with Rapier debris chunks and rock clods flying'): n loose
   *  rock pieces from the pre-fractured rock sets thrown up and out of a fresh crater at (x, z) of radius r (power = launch
   *  speed), plus a ring of rubble left on the rim. Render-only; pieces are ordinary chunks (collide, tumble, settle, sink). */
  erupt(x: number, z: number, r: number, n: number, power: number): void {
    if (!this.ready || !this.world) return;
    const tpl = this.templates.get('rock:crumble') ?? this.templates.get('rock:shatter'), mats = this.mats.get('rock');
    if (!tpl || !tpl.length || !mats) return;
    const m = Math.max(0, Math.min(n, this.cap - 6)), room = this.cap - this.chunks.length;
    if (room < m) this.evict(m - room);
    const gy = this.ground.sample(x, z), big = Math.min(1.6, 0.8 + r * 0.12);
    for (let k = 0; k < m; k++) {
      const t = tpl[(k * 7 + 3) % tpl.length], a = Math.random() * 6.283, d = Math.sqrt(Math.random()) * r * 0.55;
      const s = rnd(0.55, 1.25) * big; _s.set(s * rnd(0.8, 1.2), s * rnd(0.7, 1.1), s * rnd(0.8, 1.2));
      _p.set(x + Math.cos(a) * d, gy + 0.3 + Math.random() * 0.6, z + Math.sin(a) * d);
      _q.setFromEuler(_e.set(Math.random() * 6.283, Math.random() * 6.283, Math.random() * 6.283));
      const c = this.piece(t.geo, t.line, t.hull, t.vol, mats, _p, _q, _s, 'rock', rnd(6, 9), 0);
      const out = power * rnd(0.25, 0.7) * (0.4 + 0.6 * d / Math.max(0.1, r * 0.55)), up = power * rnd(0.7, 1.3) / Math.max(0.7, s);
      const rv = new Float32Array([Math.cos(a) * out, up, Math.sin(a) * out, rnd(-1, 1) * 10, rnd(-1, 1) * 10, rnd(-1, 1) * 10]);
      this.launch(c.body, rv);
    }
    this.rubblePile(x, z, r * 1.05, Math.round(8 + r * 1.5), 'rock', 0.9);
  }

  /** Break a prop. world = its drawn transform; (ox, oz) = damage origin, power = fling speed, src = PROP_SRC,
   *  (dx, dz) = travel direction of the cause (0,0 = radial). */
  breakProp(p: Prop, world: THREE.Matrix4, ox: number, oz: number, power: number, src: number = PROP_SRC.blast, dx = 0, dz = 0): void {
    if (!this.ready || !this.world) return;
    // direction the damage travelled through the prop: along a beam / blade / wall, else away from the origin
    let fx = dx, fz = dz;
    if (!fx && !fz) { fx = p.x - ox; fz = p.z - oz; }
    const fl = Math.hypot(fx, fz); if (fl > 1e-4) { fx /= fl; fz /= fl; } else { const a = Math.random() * 6.283; fx = Math.cos(a); fz = Math.sin(a); }
    if (p.kind === 'tree') this.breakTree(p, world, ox, oz, power, src, fx, fz);
    else if (p.kind === 'bush') this.breakBush(p, world, power, src, fx, fz);
    else this.breakRock(p, world, ox, oz, power, src, fx, fz);
  }

  /** the variant shape of a tree / bush */
  private flora(p: Prop): FloraShape | null {
    const list = p.kind === 'tree' ? this.sources.trees : p.kind === 'bush' ? this.sources.bushes : null;
    return list ? list[p.v] ?? list[0] ?? null : null;
  }

  // ---------------------------------------------------------------- trees
  private treeGroup(v: number, cut: number, charred: boolean): THREE.Group {
    const key = `${v}|${cut}|${charred}`;
    let g = this.treeTpl.get(key);
    if (g) return g;
    g = new THREE.Group();
    const f = this.sources.trees?.[v] ?? this.sources.trees?.[0], parts = f?.parts ?? this.sources.treeParts;
    const tR = (y: number) => (f ? trunkRadius(f, y) : trunkR(y));
    if (!parts) { g.add(this.sources.tree.clone(true)); this.treeTpl.set(key, g); return g; }
    parts.forEach((tp, idx) => {
      let geo = tp.geo;
      if (idx === 0 && cut > 0) { // clamp the trunk below the cut onto the cut plane: a clean flat bottom at the cut
        geo = _own(tp.geo.clone(), this.tplGeos); const pos = geo.attributes.position as THREE.BufferAttribute, rc = tR(cut);
        for (let i = 0; i < pos.count; i++) if (pos.getY(i) < cut) { const x = pos.getX(i), z = pos.getZ(i), r = Math.hypot(x, z); pos.setXYZ(i, r > 1e-5 ? x / r * rc : 0, cut, r > 1e-5 ? z / r * rc : 0); }
        geo.computeVertexNormals();
      }
      const leaf = idx === parts.length - 1 && idx > 0;
      const mat = charred ? this.charMat(tp.mat, leaf) : tp.mat;
      const mesh = new THREE.Mesh(geo, mat); mesh.castShadow = !leaf || !!tp.geo.getAttribute('core'); g!.add(mesh);
      if (this.sources.cel && tp.outline > 0) g!.add(new THREE.Mesh(_own(outlineGeometry(geo), this.tplGeos), outlineMaterial(tp.outline)));
    });
    if (cut > 0) { // the cut face under the falling top: pale wood (charred: black with embers)
      const face = new THREE.Mesh(_own(new THREE.CircleGeometry(tR(cut) * 0.97, 16).rotateX(Math.PI / 2).translate(0, cut - 0.002, 0), this.tplGeos), charred ? this.charMat(parts[0].mat, false) : this.woodMat());
      g.add(face);
    }
    this.treeTpl.set(key, g);
    return g;
  }

  private breakTree(p: Prop, world: THREE.Matrix4, ox: number, oz: number, power: number, src: number, fx: number, fz: number): void {
    const W = this.world!;
    if (this.chunks.length >= this.cap) this.evict(1);
    world.decompose(_p, _q, _s);
    // S = vertical scale (heights), Sw = horizontal (radii): variants are drawn with a per-tree width / height factor
    const P = _p.clone(), Q = _q.clone(), SV = _s.clone(), S = _s.y, Sw = _s.x, bx = p.x, bz = p.z, gy = this.ground.sample(bx, bz);
    const f = this.flora(p), v = f ? p.v : 0, CY = f?.canopyY ?? CANOPY_Y, CR = f?.canopyR ?? 1.15, TH = f?.trunkH ?? TRUNK_H;
    const tR = (y: number) => (f ? trunkRadius(f, y) : trunkR(y)), wk = (f?.r0 ?? TRUNK_R0) / TRUNK_R0;
    const laser = src === PROP_SRC.beam || src === PROP_SRC.pierce, burnt = src === PROP_SRC.burn || p.char > 0.5;
    const uproot = src === PROP_SRC.blast || src === PROP_SRC.wall || src === PROP_SRC.vortex;
    const cut = src === PROP_SRC.cut ? CUT.disc : laser ? CUT.laser : uproot ? 0 : CUT.base;
    const charred = burnt || laser || (src === PROP_SRC.blast && power >= 10);
    const tree = this.treeGroup(v, cut, charred).clone(false);
    for (const ch of this.treeGroup(v, cut, charred).children) tree.add(ch.clone(false));
    tree.scale.copy(SV); tree.position.copy(P); tree.quaternion.copy(Q);
    // stump left behind (charred when burnt / lasered), or a torn-up root hole
    if (cut > 0) {
      _c.setRGB(1, 1, 1); if (burnt) _c.setRGB(0.18, 0.15, 0.13); else if (laser) _c.setRGB(0.5, 0.42, 0.36);
      this.stumps.add(bx, bz, 0, p.rot, Sw * wk, S * cut / STUMP_H, Sw * wk, _c);
      const h = cut * S, col = W.createCollider(RAPIER.ColliderDesc.cylinder(h / 2, tR(cut) * Sw).setTranslation(bx, gy + h / 2, bz).setFriction(0.35));
      this.stumpCols.push({ col, t: 6 });
    } else if (src !== PROP_SRC.vortex) {
      this.ground.stampCrater(bx, bz, 0.45 + 0.35 * S * Math.sqrt(wk), 0.7);
      this.fx?.blast?.pebbles(bx, gy + 0.3, bz, 10, fx * 2, 5, fz * 2, 3, 0.12, DEBRIS.look.clod, 1.6); // dirt clods
    }
    const hinge = !uproot && src !== PROP_SRC.cut;
    const desc = (hinge ? RAPIER.RigidBodyDesc.kinematicPositionBased() : RAPIER.RigidBodyDesc.dynamic()).setTranslation(P.x, P.y + (uproot ? 0.15 : 0), P.z).setRotation({ x: Q.x, y: Q.y, z: Q.z, w: Q.w })
      .setLinearDamping(0.25).setAngularDamping(0.5).setCanSleep(true);
    const body = W.createRigidBody(desc);
    const y0 = Math.max(cut, 0.02) * S, y1 = Math.max(y0 + 0.2, Math.min(TH * 1.03, CY - CR * 0.5) * S);
    const c1 = W.createCollider(RAPIER.ColliderDesc.cylinder((y1 - y0) / 2, Math.max(0.06, tR(TH * 0.5) * 0.9) * Sw).setTranslation(0, (y0 + y1) / 2, 0).setDensity(0.9).setFriction(src === PROP_SRC.cut ? 0.35 : 0.9).setRestitution(0.2), body);
    W.createCollider(RAPIER.ColliderDesc.ball(CR * Sw).setTranslation(0, CY * S, 0).setDensity(0.22).setFriction(0.9).setRestitution(0.25), body);
    this.scene.add(tree);
    const c = this.chunk(tree, body, c1, 'tree', rnd(7, 10), S * 2);
    c.tree = true; c.S = S; c.cy = CY; c.top = f?.top ?? 4; c.ash = charred && src !== PROP_SRC.blast && src !== PROP_SRC.vortex;
    const ax = fz, az = -fx; // rotation axis that tips the tree along (fx, fz)
    if (src === PROP_SRC.cut) { // blade: the top slides off the stump along the cut and topples
      body.setLinvel({ x: fx * rnd(3.2, 4.5), y: 0.6, z: fz * rnd(3.2, 4.5) }, true);
      body.setAngvel({ x: ax * 1.1, y: rnd(-0.3, 0.3), z: az * 1.1 }, true);
      this.leaves.emit(bx, gy + CY * S, bz, 14, S, 1);
    } else if (uproot) {
      if (src === PROP_SRC.vortex) this.pullTo(c, bx, bz);
      else {
        const k = src === PROP_SRC.wall ? 1 : Math.min(1.6, power / 11);
        const out = src === PROP_SRC.wall ? rnd(6, 8) : power * rnd(0.35, 0.5), up = src === PROP_SRC.wall ? rnd(6, 8) : 3 + power * rnd(0.2, 0.32);
        body.setLinvel({ x: fx * out, y: up, z: fz * out }, true);
        body.setAngvel({ x: ax * rnd(2, 3.5) * k, y: rnd(-1, 1), z: az * rnd(2, 3.5) * k }, true);
        this.leaves.emit(bx, gy + CY * S, bz, 26, S * 1.2, 3, charred);
      }
    } else { // hinge at the cut edge on the fall side; gravity tips it over (θ'' = g/L·sinθ), released near 70°
      const r = tR(Math.max(cut, 0.05)) * Sw;
      c.hinge = { px: bx + fx * r, py: gy + cut * S, pz: bz + fz * r, ax, az, th: 0.025, w: laser ? 0.35 : src === PROP_SRC.burn ? 0.15 : 0.28, k: G / (CY * S * 0.8), q0: Q, p0: P };
      if (laser) this.fx?.particles.emit(bx, gy + cut * S, bz, 12, 3, 0xff8a2a, 0.25, 0.5, 1.5, 4, 2, 3);
      this.leaves.emit(bx, gy + CY * S, bz, burnt ? 6 : 10, S, 0.6, burnt);
    }
    this.dust(bx, gy + 0.3, bz, uproot ? 4 : 2, 1.2, fx, fz, burnt ? 1.5 : 0);
  }

  // ---------------------------------------------------------------- bushes
  /** the bush mesh for a variant; cut > 0 flattens everything below that height (local) onto the cut plane */
  private bushGroup(v: number, cut: number, charred: boolean): THREE.Group {
    const key = `${v}|${cut}|${charred}`;
    let g = this.bushTpl.get(key); if (g) return g;
    g = new THREE.Group();
    const f = this.sources.bushes?.[v] ?? this.sources.bushes?.[0];
    for (const tp of f?.parts ?? []) {
      let geo = tp.geo;
      if (cut > 0) { geo = _own(tp.geo.clone(), this.tplGeos); const pos = geo.attributes.position as THREE.BufferAttribute; for (let i = 0; i < pos.count; i++) if (pos.getY(i) < cut) pos.setY(i, cut); }
      const mesh = new THREE.Mesh(geo, charred ? this.charMat(tp.mat, true) : tp.mat); mesh.castShadow = true; g.add(mesh);
    }
    this.bushTpl.set(key, g);
    return g;
  }

  /** Bushes (owner 2026-11-02 'add some bushes too'): shred into leaves + twigs; the disc slices the top off and it slides
   *  away; blasts / Riftline tear the whole bush out and toss it; the Singularity sucks it in; beams, needles and fire burn it
   *  to ash. Each leaves a stubble of snapped twigs (charred + a burnt patch when burnt). */
  private breakBush(p: Prop, world: THREE.Matrix4, power: number, src: number, fx: number, fz: number): void {
    const f = this.flora(p); if (!f) return;
    world.decompose(_p, _q, _s);
    const P = _p.clone(), Q = _q.clone(), SV = _s.clone(), gy = this.ground.sample(p.x, p.z);
    const laser = src === PROP_SRC.beam || src === PROP_SRC.pierce, burnt = src === PROP_SRC.burn || p.char > 0.5;
    const charred = burnt || laser || (src === PROP_SRC.blast && power >= 12);
    const uproot = src === PROP_SRC.blast || src === PROP_SRC.wall || src === PROP_SRC.vortex, cutSrc = src === PROP_SRC.cut;
    const cy = gy + f.canopyY * SV.y, spread = f.canopyR * SV.x;
    // the shred: a burst of leaves and snapped twigs (ash flakes + embers when burnt)
    this.leaves.emit(p.x, cy, p.z, charred ? 12 : uproot ? 30 : 22, spread, uproot ? 2.5 : 1.4, charred);
    this.fx?.blast?.pebbles(p.x, cy, p.z, charred ? 4 : 7, fx * (uproot ? 4 : 1.5), 3, fz * (uproot ? 4 : 1.5), 2.5, 0.045, charred ? DEBRIS.look.ash[0] : DEBRIS.look.clod, 1.4);
    if (charred && this.fx) for (let k = 0; k < 4; k++) this.fx.particles.emitDir(p.x + rnd(-0.5, 0.5) * spread, cy, p.z + rnd(-0.5, 0.5) * spread, rnd(-0.4, 0.4), rnd(1, 2.2), rnd(-0.4, 0.4), 0xff6a1c, 0.14, 1, 3);
    // what stays: twig stubble (+ a scorched patch and ash when burnt; torn-out roots leave a little dirt hole)
    _c.setRGB(1, 1, 1); if (charred) _c.setRGB(0.2, 0.17, 0.15);
    if (!uproot || src === PROP_SRC.vortex) this.brush.add(p.x, p.z, 0, p.rot, SV.x * 1.3, SV.y * (cutSrc ? 1.4 : 1), SV.z * 1.3, _c);
    if (charred) {
      this.ground.stampBurn(p.x, p.z, p.r * 1.25);
      for (let k = 0; k < 3; k++) { _c.setHex(DEBRIS.look.ash[k % DEBRIS.look.ash.length]); const a = Math.random() * 6.283, d = Math.random() * p.r * 0.6; this.rubble.add(p.x + Math.cos(a) * d, p.z + Math.sin(a) * d, 0.01, a, 0.3 * SV.x, 0.08, 0.25 * SV.x, _c, rnd(-0.2, 0.2)); }
      this.dust(p.x, gy + 0.3, p.z, 1, 0.8, 0, 0, 1.5);
      return;
    }
    if (uproot && src !== PROP_SRC.vortex) { this.ground.stampCrater(p.x, p.z, 0.35 + 0.3 * SV.x, 0.5); this.fx?.blast?.pebbles(p.x, gy + 0.2, p.z, 5, fx * 2, 4, fz * 2, 2.5, 0.08, DEBRIS.look.clod, 1.4); }
    if (!cutSrc && !uproot) { this.dust(p.x, gy + 0.3, p.z, 1, 0.8, fx, fz, 0); return; } // chip / crush: shredded where it stood
    // a body: the sliced-off top (disc) or the whole bush (blast / wall / vortex)
    if (this.chunks.length >= this.cap) this.evict(1);
    const W = this.world!, cutY = cutSrc ? f.top * 0.32 : 0;
    const tpl = this.bushGroup(p.v, cutY, false), g = tpl.clone(false); for (const ch of tpl.children) g.add(ch.clone(false));
    g.scale.copy(SV); g.position.copy(P); g.quaternion.copy(Q);
    const body = W.createRigidBody(RAPIER.RigidBodyDesc.dynamic().setTranslation(P.x, P.y + (cutSrc ? 0.04 : 0.15), P.z).setRotation({ x: Q.x, y: Q.y, z: Q.z, w: Q.w }).setLinearDamping(0.6).setAngularDamping(0.8).setCanSleep(true));
    const col = W.createCollider(RAPIER.ColliderDesc.ball(Math.max(0.2, spread * 0.75)).setTranslation(0, Math.max(cutY * SV.y + 0.2, f.canopyY * SV.y), 0).setDensity(0.3).setFriction(0.9).setRestitution(0.15), body);
    this.scene.add(g);
    const c = this.chunk(g, body, col, 'bush', rnd(2.6, 3.6), spread);
    if (src === PROP_SRC.vortex) { this.pullTo(c, p.x, p.z); return; }
    if (cutSrc) { body.setLinvel({ x: fx * rnd(2.8, 3.8), y: 0.8, z: fz * rnd(2.8, 3.8) }, true); body.setAngvel({ x: fz * 2, y: rnd(-1, 1), z: -fx * 2 }, true); return; }
    const out = src === PROP_SRC.wall ? rnd(5, 7) : power * rnd(0.4, 0.6), up = src === PROP_SRC.wall ? rnd(5, 7) : 3 + power * rnd(0.25, 0.4);
    body.setLinvel({ x: fx * out, y: up, z: fz * out }, true); body.setAngvel({ x: fz * rnd(3, 6), y: rnd(-2, 2), z: -fx * rnd(3, 6) }, true);
  }

  // ---------------------------------------------------------------- rocks, boulders, spires
  private breakRock(p: Prop, world: THREE.Matrix4, ox: number, oz: number, power: number, src: number, fx: number, fz: number): void {
    const kind = p.kind as RockKind, mats = this.mats.get(kind); if (!mats) return;
    world.decompose(_p, _q, _s);
    const P = _p.clone(), Q = _q.clone(), S = _s.clone(), gy = this.ground.sample(p.x, p.z);
    const spire = kind === 'spire', big = spire ? 3 : kind === 'boulder' ? 1.6 : 1;
    const laser = src === PROP_SRC.beam || src === PROP_SRC.pierce;
    const height = spire ? S.y : S.y * 1.6;
    // a) blade: slice the real mesh along a plane tilted ±25° about the travel direction
    if (src === PROP_SRC.cut && this.sliceBudget > 0 && this.cutRock(p, kind, P, Q, S, fx, fz, mats)) {
      this.sliceBudget--;
      this.dust(p.x, gy + 0.4, p.z, Math.round(3 * big), p.r * 0.8, fx, fz, 0);
      this.fx?.blast?.pebbles(p.x, gy + Math.min(2, height * 0.4), p.z, Math.round(6 * big), fx * 4, 3, fz * 4, 2.5, 0.07 * big, DEBRIS.look.chip);
      return;
    }
    // b) everything else from the pre-fractured sets
    const fine = this.templates.get(`${kind}:crumble`);
    const tpl = (laser || src === PROP_SRC.vortex) && fine ? fine : this.templates.get(`${kind}:shatter`);
    if (!tpl) return;
    const room = this.cap - this.chunks.length;
    if (room < tpl.length) this.evict(tpl.length - room);
    // impact side faces the damage: yaw the assembly so the template's dense side (+X) points back at the origin
    if (tpl !== fine) {
      _v.set(1, 0, 0).applyQuaternion(Q);
      const a1 = Math.atan2(_v.z, _v.x), a2 = Math.atan2(-fz, -fx);
      if (Math.hypot(_v.x, _v.z) > 0.3) Q.premultiply(_q2.setFromAxisAngle(UP, a1 - a2));
    }
    const hot = laser ? this.hotMat(kind, src === PROP_SRC.pierce ? 0xff3a22 : 0xff8a2a) : null;
    const m = hot ? [mats[0], hot] : mats;
    const life = spire ? rnd(8, 10) : rnd(6, 9);
    const centre = _w.copy(P).add(_u.set(0, spire ? height * 0.3 : 0, 0));
    for (const t of tpl) {
      const at = _v.copy(t.offset).multiply(S).applyQuaternion(Q).add(P);
      // laser: pieces sit apart by a hair so the glowing seams show before they fall
      if (hot) { _n.copy(at).sub(centre).normalize(); at.addScaledVector(_n, 0.03 + 0.012 * Math.cbrt(t.vol * S.x * S.y * S.z) * 4); }
      const mass = Math.cbrt(t.vol * S.x * S.y * S.z), light = Math.min(1.5, Math.max(0.45, 1.3 - mass * 0.35)); // small bits fly faster
      // spires collapse in sections: the base gives way first, each tier drops after it over ~1 s
      const hold = spire ? (laser ? 0.2 : 0.05) + t.ny * (src === PROP_SRC.vortex ? 0.6 : 1.0) : laser ? 0.22 : 0;
      const rv = new Float32Array(6);
      _n.copy(at).sub(_u.set(p.x, at.y - (spire ? 0.5 : 0.8), p.z)).normalize(); // outward from the prop's axis
      if (src === PROP_SRC.blast) {
        _u.set(at.x - ox, 0, at.z - oz).normalize();
        const sp = power * rnd(0.75, 1.35) * light * (spire ? 0.55 + 0.45 * (1 - t.ny) : 1);
        rv[0] = _u.x * sp + _n.x * 2; rv[1] = rnd(2.5, 5) + power * rnd(0.15, 0.4) * light; rv[2] = _u.z * sp + _n.z * 2;
      } else if (src === PROP_SRC.wall) {
        rv[0] = fx * rnd(5, 9) * light + _n.x * 1.5; rv[1] = rnd(6, 11) * light; rv[2] = fz * rnd(5, 9) * light + _n.z * 1.5;
      } else if (laser) { // crumble: they slump and roll off, barely thrown
        rv[0] = _n.x * rnd(0.6, 2) + fx * 0.8; rv[1] = rnd(-0.5, 0.8); rv[2] = _n.z * rnd(0.6, 2) + fz * 0.8;
      } else { // chip / crush / other: a heavy break, pieces fall out and roll
        const sp = rnd(1.5, 3.5) * light;
        rv[0] = _n.x * sp + fx * 1.5; rv[1] = rnd(0.5, 2.5); rv[2] = _n.z * sp + fz * 1.5;
      }
      if (spire && src !== PROP_SRC.blast) { rv[0] += _n.x * 2.5 * (1 - t.ny); rv[2] += _n.z * 2.5 * (1 - t.ny); } // the base bursts outward
      const spin = laser ? 2 : src === PROP_SRC.blast ? 9 : 5;
      rv[3] = rnd(-1, 1) * spin; rv[4] = rnd(-1, 1) * spin; rv[5] = rnd(-1, 1) * spin;
      const c = this.piece(t.geo, t.line, t.hull, t.vol, m, at, Q, S, kind, life * rnd(0.85, 1.1), hold);
      if (src === PROP_SRC.vortex) { this.pullTo(c, p.x, p.z); c.hold = hold; }
      if (hold > 0) c.rv = rv; else this.launch(c.body, rv);
    }
    // laser: a glowing cut line along the beam through the rock, then fine hot grit
    if (laser) {
      const yc = gy + Math.min(1.6, height * 0.45);
      for (let k = -4; k <= 4; k++) this.fx?.particles.emitDir(p.x + fx * k * p.r * 0.25, yc, p.z + fz * k * p.r * 0.25, fx * 2 + rnd(-1, 1), rnd(0, 2), fz * 2 + rnd(-1, 1), src === PROP_SRC.pierce ? 0xff3a22 : 0xffa040, 0.28, 0.6, 3);
    }
    // dust cloud (heavier for spires) + fine gravel spray, thrown along the hit
    const dk = src === PROP_SRC.blast ? Math.min(1.6, power / 12) : 1;
    this.dust(p.x, gy + (spire ? 1.5 : 0.4), p.z, Math.round((spire ? 14 : kind === 'boulder' ? 6 : 3) * dk), p.r * (spire ? 1.2 : 0.8), fx, fz, laser ? 0.6 : 0);
    this.fx?.blast?.pebbles(p.x, gy + Math.min(2.5, height * 0.4), p.z, Math.round(7 * big * dk), fx * (src === PROP_SRC.blast ? power * 0.6 : 3), 4, fz * (src === PROP_SRC.blast ? power * 0.6 : 3), 3 + big, 0.06 * big, laser ? 0x7a4a2a : DEBRIS.look.chip);
    // a rubble pile stays where it stood
    this.rubblePile(p.x, p.z, p.r * (spire ? 1.1 : 0.9), Math.round(spire ? 22 : kind === 'boulder' ? 9 : 4), kind, laser ? 0.55 : 1);
    if (spire) { if (this.fx) this.fx.shake = Math.min(1, this.fx.shake + 0.5); }
    else if (kind === 'boulder' && this.fx) this.fx.shake = Math.min(1, this.fx.shake + 0.15);
  }

  /** Scythe Disc: slice the prop's own mesh in two along the blade's plane; the top half slides off along the cut. */
  private cutRock(p: Prop, kind: RockKind, P: THREE.Vector3, Q: THREE.Quaternion, S: THREE.Vector3, fx: number, fz: number, mats: THREE.Material[]): boolean {
    const src = this.sliceSrc.get(kind); if (!src) return false;
    const spire = kind === 'spire';
    // plane: horizontal, tilted ±25° about the travel axis, through the blade height (spires) / just above the middle (rocks)
    const side = Math.random() < 0.5 ? 1 : -1, tilt = side * rnd(0.3, 0.45);
    const n = _n.set(0, 1, 0).applyAxisAngle(_u.set(fx, 0, fz), tilt);
    const gy = this.ground.sample(p.x, p.z);
    const o = _w.set(p.x, spire ? gy + Math.min(S.y * 0.5, 1.6) : P.y + S.y * 0.12, p.z);
    // world plane → local plane (normal by the transpose of the linear part, which handles the non-uniform scale)
    _m.compose(P, Q, S);
    const lin = new THREE.Matrix3().setFromMatrix4(_m), ln = n.clone().applyMatrix3(lin.clone().transpose()).normalize();
    const lo = o.clone().applyMatrix4(_m.clone().invert());
    let pieces: THREE.BufferGeometry[];
    try { pieces = new DestructibleMesh(src as never).slice(ln, lo).map((d) => d.geometry as unknown as THREE.BufferGeometry); } catch { return false; }
    if (pieces.length < 2) return false;
    src.computeBoundingBox();
    const room = this.cap - this.chunks.length; if (room < pieces.length) this.evict(pieces.length - room);
    const sx = -fz * side, sz = fx * side; // downhill direction on the tilted plane
    for (const g of pieces) {
      const t = this.toTemplate(g, src.boundingBox!, !spire);
      const at = _v.copy(t.offset).multiply(S).applyQuaternion(Q).add(P);
      const top = (at.x - o.x) * n.x + (at.y - o.y) * n.y + (at.z - o.z) * n.z > 0;
      const c = this.piece(t.geo, t.line, t.hull, t.vol, mats, at, Q, S, kind, spire ? rnd(9, 11) : rnd(7, 9), 0);
      c.own = t.geo; c.ownLine = t.line;
      const rv = new Float32Array(6);
      if (top) {
        const k = spire ? 0.6 : 1;
        rv[0] = (fx * rnd(2.5, 3.5) + sx * rnd(1, 2)) * k; rv[1] = spire ? 0.3 : 1.2; rv[2] = (fz * rnd(2.5, 3.5) + sz * rnd(1, 2)) * k;
        rv[3] = fz * 0.8 * side; rv[5] = -fx * 0.8 * side; rv[4] = rnd(-0.4, 0.4);
        if (spire) { c.spireTop = t.offset.clone(); c.spireS = S.clone(); }
      } else { rv[0] = -sx * 0.4; rv[2] = -sz * 0.4; }
      this.launch(c.body, rv);
    }
    return true;
  }

  private piece(geo: THREE.BufferGeometry, line: THREE.BufferGeometry | null, hull0: Float32Array, vol: number, mats: THREE.Material[], at: THREE.Vector3, Q: THREE.Quaternion, S: THREE.Vector3, kind: PropKind, life: number, hold: number): Chunk {
    const W = this.world!;
    const mesh = new THREE.Mesh(geo, mats);
    mesh.castShadow = true; mesh.scale.copy(S); mesh.position.copy(at); mesh.quaternion.copy(Q);
    if (line) { const avg = (S.x + S.y + S.z) / 3; mesh.add(new THREE.Mesh(line, outlineMaterial(avg > 1.6 ? 0.012 : avg > 0.9 ? 0.022 : 0.04))); }
    const desc = (hold > 0 ? RAPIER.RigidBodyDesc.kinematicPositionBased() : RAPIER.RigidBodyDesc.dynamic())
      .setTranslation(at.x, at.y + 0.04, at.z).setRotation({ x: Q.x, y: Q.y, z: Q.z, w: Q.w })
      .setLinearDamping(0.12).setAngularDamping(0.35).setCanSleep(true);
    const body = W.createRigidBody(desc);
    const hull = new Float32Array(hull0.length);
    for (let i = 0; i < hull.length; i += 3) { hull[i] = hull0[i] * S.x; hull[i + 1] = hull0[i + 1] * S.y; hull[i + 2] = hull0[i + 2] * S.z; }
    const size = Math.cbrt(vol * S.x * S.y * S.z);
    const cd = RAPIER.ColliderDesc.convexHull(hull) ?? RAPIER.ColliderDesc.ball(size * 0.4);
    const col = W.createCollider(cd.setDensity(2.4).setFriction(0.85).setRestitution(0.18), body);
    this.scene.add(mesh);
    const c = this.chunk(mesh, body, col, kind, life, size);
    c.hold = hold;
    return c;
  }

  private chunk(mesh: THREE.Object3D, body: RT.RigidBody, col: RT.Collider | null, kind: PropKind, life: number, size: number): Chunk {
    const c: Chunk = { mesh, body, col, age: 0, life, sink: 0, hold: 0, rv: null, pull: 0, tx: 0, ty: 0, tz: 0, hinge: null, bs: null, tree: false, S: 1, impact: 0, ash: false, ashT: 0, cy: CANOPY_Y, top: 4, spireTop: null, spireS: null, own: null, ownLine: null, kind, size };
    this.chunks.push(c);
    return c;
  }

  private launch(body: RT.RigidBody, rv: Float32Array): void {
    body.setLinvel({ x: rv[0], y: rv[1], z: rv[2] }, true);
    body.setAngvel({ x: rv[3], y: rv[4], z: rv[5] }, true);
  }

  private pullTo(c: Chunk, x: number, z: number): void {
    c.pull = 1; c.tx = x; c.ty = this.ground.sample(x, z) + 2.5; c.tz = z; c.bs = c.mesh.scale.clone(); c.life = 4;
    c.body.setGravityScale(0, true); c.col?.setCollisionGroups(0);
    if (this.orbAt && this.orbAt(x, z, _u)) { c.tx = _u.x; c.ty = _u.y; c.tz = _u.z; }
  }

  private dust(x: number, y: number, z: number, n: number, spread: number, fx: number, fz: number, tone: number): void {
    const b = this.fx?.blast; if (!b) return;
    for (let k = 0; k < n; k++) {
      const a = Math.random() * 6.283, r = Math.random() * spread;
      b.dust(x + Math.cos(a) * r, y + Math.random() * spread * 0.6, z + Math.sin(a) * r, Math.cos(a) * rnd(0.6, 1.8) + fx * 1.2, rnd(0.8, 2.4), Math.sin(a) * rnd(0.6, 1.8) + fz * 1.2,
        rnd(2.2, 3.8), spread * 0.5 + 0.5, spread * 1.4 + rnd(1, 2.2), tone);
    }
  }

  private rubblePile(x: number, z: number, r: number, n: number, kind: PropKind, tone: number): void {
    const R = DEBRIS.look.rubble, base = kind === 'spire' ? R[0] : kind === 'boulder' ? R[1] : R[2];
    for (let k = 0; k < n; k++) {
      const a = Math.random() * 6.283, d = Math.sqrt(Math.random()) * r * 1.3, s = (kind === 'spire' ? rnd(0.25, 0.7) : rnd(0.12, 0.32)) * (k < n * 0.2 ? 1.6 : 1);
      _c.setHex(base).multiplyScalar(rnd(0.7, 1.05) * tone);
      this.rubble.add(x + Math.cos(a) * d, z + Math.sin(a) * d, s * 0.12, Math.random() * 6.283, s * rnd(0.9, 1.4), s * rnd(0.6, 1), s, _c, rnd(-0.3, 0.3));
    }
  }

  private evict(n: number): void {
    for (let k = 0; k < n && this.chunks.length; k++) this.remove(this.chunks.shift()!);
  }
  private remove(c: Chunk): void {
    this.scene.remove(c.mesh);
    this.world?.removeRigidBody(c.body);
    c.own?.dispose(); c.ownLine?.dispose();
  }

  clear(): void {
    for (const c of this.chunks) this.remove(c);
    this.chunks.length = 0; this.groundVersion = -1;
    for (const s of this.stumpCols) this.world?.removeCollider(s.col, false);
    this.stumpCols.length = 0; this.leaves.clear(); this.rubble.clear(); this.stumps.clear(); this.brush.clear();
  }

  /** a fallen tree lands: leaves burst + dust (charred: crumbles to ash a moment later) */
  private land(c: Chunk, cx: number, cy: number, cz: number): void {
    c.impact = 1;
    c.body.setAngularDamping(3); // the round canopy collider would otherwise keep rolling the tree over onto its back
    const gy = this.ground.sample(cx, cz);
    this.leaves.emit(cx, cy, cz, c.ash ? 8 : 34, c.S * 1.1, 2.5, c.ash);
    this.dust(cx, gy + 0.3, cz, 4, c.S * 0.8, 0, 0, c.ash ? 1.5 : 0);
    if (this.fx) this.fx.shake = Math.min(1, this.fx.shake + 0.08);
    if (c.ash) c.ashT = 0.45;
  }

  /** charred tree: breaks up into ash-coloured bits along the trunk and a pile of ash */
  private toAsh(c: Chunk): void {
    const pos = c.mesh.position, q = c.mesh.quaternion, S = c.S;
    _u.set(0, 1, 0).applyQuaternion(q);
    for (let k = 0; k < 8; k++) {
      const h = (0.3 + k / 8 * (c.top - 0.6)) * S, x = pos.x + _u.x * h, y = pos.y + _u.y * h, z = pos.z + _u.z * h;
      this.fx?.blast?.pebbles(x, y + 0.2, z, 3, 0, 2, 0, 1.5, 0.09 * S, DEBRIS.look.ash[k % DEBRIS.look.ash.length], 2.2);
      _c.setHex(DEBRIS.look.ash[k % DEBRIS.look.ash.length]);
      if (k % 2 === 0) this.rubble.add(x, z, 0.02, Math.random() * 6.283, 0.35 * S, 0.12 * S, 0.3 * S, _c, rnd(-0.2, 0.2));
      if (k % 3 === 0) this.fx?.blast?.dust(x, y, z, 0, rnd(0.6, 1.4), 0, rnd(2, 3), 0.6, 2.2, 1.6);
      if (this.fx && k % 2 === 0) this.fx.particles.emitDir(x, y + 0.3, z, rnd(-0.5, 0.5), rnd(1, 2.5), rnd(-0.5, 0.5), 0xff6a1c, 0.18, 1.2, 3);
    }
    c.life = 0; c.sink = 99; // removed this frame
  }

  update(dt: number): void {
    if (!this.ready || !this.world) return;
    this.clock += dt; this.sliceBudget = 2;
    this.sinceGround += dt;
    if (this.ground.version !== this.groundVersion && this.sinceGround > 0.5) { this.sinceGround = 0; this.rebuildGround(); }
    if ((this.reseatT += dt) > 1) { this.reseatT = 0; this.rubble.reseat(); this.stumps.reseat(); this.brush.reseat(); }
    for (const pool of this.hot.values()) for (const h of pool) { const u = Math.max(0, 1 - (this.clock - h.t0) / h.dur); h.heat.value = u; }
    for (let k = this.stumpCols.length - 1; k >= 0; k--) { const s = this.stumpCols[k]; if ((s.t -= dt) <= 0) { this.world.removeCollider(s.col, false); this.stumpCols.splice(k, 1); } }
    // held pieces (laser seams, spire tiers) and hinged trees move before the physics step
    for (const c of this.chunks) {
      if (c.hold > 0) {
        c.hold -= dt;
        if (c.hold <= 0) {
          c.body.setBodyType(RAPIER.RigidBodyType.Dynamic, true);
          if (c.pull) c.body.setGravityScale(0, true);
          if (c.rv) { this.launch(c.body, c.rv); c.rv = null; }
          if (c.kind === 'spire' && Math.random() < 0.35) { const t = c.body.translation(); this.dust(t.x, t.y, t.z, 1, 1.5, 0, 0, 0); }
        }
      }
      const h = c.hinge;
      if (h) {
        h.w += h.k * Math.sin(h.th) * dt; h.th += h.w * dt;
        _q2.setFromAxisAngle(_u.set(h.ax, 0, h.az), Math.min(h.th, 1.25));
        _q.copy(_q2).multiply(h.q0);
        _v.copy(h.p0).sub(_w.set(h.px, h.py, h.pz)).applyQuaternion(_q2).add(_w);
        c.body.setNextKinematicTranslation({ x: _v.x, y: _v.y, z: _v.z });
        c.body.setNextKinematicRotation({ x: _q.x, y: _q.y, z: _q.z, w: _q.w });
        if (h.th >= 1.25) { // release: carry the swing into the physics (v = ω × r at the canopy's centre of mass)
          c.body.setBodyType(RAPIER.RigidBodyType.Dynamic, true);
          _u.set(0, c.cy * c.S * 0.7, 0).applyQuaternion(_q); // pivot → centre of mass (approx.)
          _n.set(h.ax, 0, h.az).multiplyScalar(h.w);
          _s.copy(_n).cross(_u);
          c.body.setLinvel({ x: _s.x, y: _s.y, z: _s.z }, true);
          c.body.setAngvel({ x: _n.x, y: _n.y, z: _n.z }, true);
          c.hinge = null;
        }
      }
    }
    this.acc = Math.min(this.acc + dt, 3 / 60);
    let steps = 0;
    while (this.acc >= 1 / 60 && steps < 3) { this.world.timestep = 1 / 60; this.world.step(); this.acc -= 1 / 60; steps++; }
    for (let i = this.chunks.length - 1; i >= 0; i--) {
      const c = this.chunks[i];
      c.age += dt;
      const t = c.body.translation(), r = c.body.rotation();
      if (c.pull) { // Singularity: lifted and sucked into the orb, shrinking, then gone
        if (this.orbAt && this.orbAt(c.tx, c.tz, _u)) { c.tx = _u.x; c.ty = _u.y; c.tz = _u.z; }
        if (c.hold <= 0) {
          // lifted off the ground first, then drawn in faster and faster, spinning, shrinking only at the end
          _v.set(c.tx - t.x, c.ty + Math.max(0, 1.2 - c.age) * 2.5 - t.y, c.tz - t.z); const d = _v.length() || 1, sp = Math.min(15, 1 + c.age * 6 + d * 0.6);
          c.body.setLinvel({ x: _v.x / d * sp, y: _v.y / d * sp + (c.age < 0.8 ? 2.5 : 0), z: _v.z / d * sp }, true);
          c.body.setAngvel({ x: 3, y: 6, z: 2 }, true);
          if (c.bs) c.mesh.scale.copy(c.bs).multiplyScalar(Math.max(0.03, Math.min(1, d / 2)));
          if (d < 0.8 || c.age > 4) { this.remove(c); this.chunks.splice(i, 1); continue; }
        }
      }
      if (c.tree && !c.impact && !c.hinge && c.age > 0.2) { // landed? canopy low and tipped over
        _u.set(0, 1, 0).applyQuaternion(_q.set(r.x, r.y, r.z, r.w));
        const cx = t.x + _u.x * c.cy * c.S, cy = t.y + _u.y * c.cy * c.S, cz = t.z + _u.z * c.cy * c.S;
        if (_u.y < 0.5 && cy - this.ground.sample(cx, cz) < 1.5 * c.S) this.land(c, cx, cy, cz);
      }
      // A felled tree must not end up canopy-down with its trunk pointing at the sky (the canopy's dark underside then reads as a
      // navy disc around the cut face, QA 2026-11-03): a top that comes to rest past horizontal (on the ground, or propped on a
      // prop or another piece) is rolled back onto its side.
      if (c.tree && !c.hinge && !c.pull && c.age > 0.6 && c.age <= c.life && c.body.bodyType() === RAPIER.RigidBodyType.Dynamic) {
        _u.set(0, 1, 0).applyQuaternion(_q.set(r.x, r.y, r.z, r.w));
        const lv = c.body.linvel();
        if (_u.y < -0.1 && lv.x * lv.x + lv.y * lv.y + lv.z * lv.z < 4) {
          _n.set(-_u.z, 0, _u.x); if (_n.lengthSq() < 1e-4) _n.set(1, 0, 0); _n.normalize(); // u × up: rotating about it lifts u
          const k = 1.5 + 2 * -_u.y, av = c.body.angvel();
          c.body.setAngvel({ x: _n.x * k, y: av.y * 0.5, z: _n.z * k }, true);
        }
      }
      if (c.ashT > 0 && (c.ashT -= dt) <= 0) this.toAsh(c);
      if (c.spireTop && c.spireS && c.age > 0.4) { // the sheared spire top crumbles when it comes down
        _u.set(0, 1, 0).applyQuaternion(_q.set(r.x, r.y, r.z, r.w));
        if (_u.y < 0.6 || c.age > 3) {
          const fine = this.templates.get('spire:crumble') ?? this.templates.get('spire:shatter');
          if (fine) {
            const lv = c.body.linvel(), Sx = c.spireS;
            _w.copy(c.spireTop).multiply(Sx).applyQuaternion(_q); const ox = t.x - _w.x, oy = t.y - _w.y, oz = t.z - _w.z; // spire origin
            const qq = _q.clone(), mats = this.mats.get('spire')!;
            this.remove(c); this.chunks.splice(i, 1);
            const room = this.cap - this.chunks.length; if (room < fine.length) this.evict(Math.min(this.chunks.length, fine.length - room));
            for (const f of fine) {
              const at = _v.copy(f.offset).multiply(Sx).applyQuaternion(qq).add(_p.set(ox, oy, oz));
              const pc = this.piece(f.geo, f.line, f.hull, f.vol, mats, at, qq, Sx, 'spire', rnd(7, 9), 0);
              this.launch(pc.body, new Float32Array([lv.x + rnd(-2, 2), lv.y + rnd(0, 3), lv.z + rnd(-2, 2), rnd(-4, 4), rnd(-4, 4), rnd(-4, 4)]));
            }
            this.dust(t.x, t.y, t.z, 10, 3, 0, 0, 0);
            if (this.fx) this.fx.shake = Math.min(1, this.fx.shake + 0.4);
            i = Math.min(i, this.chunks.length); continue;
          }
          c.spireTop = null;
        }
      }
      if (c.age > c.life) {
        if (c.sink > 50) { this.remove(c); this.chunks.splice(i, 1); continue; }
        // settled: sink into the ground and recycle
        c.sink += dt;
        if (c.body.bodyType() !== RAPIER.RigidBodyType.KinematicPositionBased) c.body.setBodyType(RAPIER.RigidBodyType.KinematicPositionBased, false);
        c.body.setNextKinematicTranslation({ x: t.x, y: t.y - dt * 0.9, z: t.z });
        if (c.sink > 1.6) { this.remove(c); this.chunks.splice(i, 1); continue; }
      }
      if (t.y < -20) { this.remove(c); this.chunks.splice(i, 1); continue; }
      if (c.hinge) { // drawn from the analytic hinge pose (the kinematic body lags a step)
        const h = c.hinge; _q2.setFromAxisAngle(_u.set(h.ax, 0, h.az), Math.min(h.th, 1.25)); _q.copy(_q2).multiply(h.q0);
        _v.copy(h.p0).sub(_w.set(h.px, h.py, h.pz)).applyQuaternion(_q2).add(_w);
        c.mesh.position.copy(_v); c.mesh.quaternion.copy(_q); continue;
      }
      c.mesh.position.set(t.x, t.y, t.z);
      c.mesh.quaternion.set(r.x, r.y, r.z, r.w);
    }
    this.leaves.update(dt);
  }

  get active(): number { return this.chunks.length; }
}
